import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["select", "row", "search", "count", "feedback", "preview", "direct", "start", "review", "stale", "optional", "empty", "filter", "unmappedCount"]
  static values = { samples: Object, previewReady: Boolean, previewing: Boolean, statusUrl: String }

  connect() {
    this.update()
    if (this.previewingValue) this.schedulePoll()
  }

  disconnect() {
    window.clearTimeout(this.pollTimer)
    this.pollRequest?.abort()
  }

  changed() {
    this.update()
  }

  update() {
    let mapped = 0
    let missing = 0
    let changed = false

    this.selectTargets.forEach(select => {
      const row = select.closest('[data-import-mapping-target="row"]')
      const sample = row.querySelector("[data-sample]")
      const values = (this.samplesValue[select.value] || []).slice(0, 2)
      mapped += select.value ? 1 : 0
      missing += select.required && !select.value ? 1 : 0
      changed ||= select.value !== select.dataset.initialValue
      row.dataset.mapped = String(Boolean(select.value))
      sample.replaceChildren()
      const labels = values.length ? values : [select.value ? "No sample values" : "—"]
      labels.forEach(value => {
        const span = document.createElement("span")
        span.textContent = value
        span.title = value
        if (!values.length) span.className = "fg-2"
        sample.append(span)
      })
    })

    this.countTarget.textContent = `${mapped} of ${this.selectTargets.length} mapped`
    this.unmappedCountTarget.textContent = this.selectTargets.length - mapped
    this.feedbackTarget.textContent = this.previewingValue ? "Checking your file…" :
      missing ? `${missing} required ${missing === 1 ? "field needs" : "fields need"} a column` :
      this.previewReadyValue && !changed ? "Your preview is up to date" :
      "Ready to import"
    this.feedbackTarget.classList.toggle("fg-emphasis-warning", missing > 0)
    this.directTarget.hidden = this.previewReadyValue && !changed
    if (this.hasStartTarget) {
      this.startTarget.disabled = changed || this.startTarget.dataset.noValidRows === "true"
      this.startTarget.hidden = changed
    }
    if (this.hasStaleTarget) this.staleTarget.classList.toggle("d-none", !changed)
    if (this.hasReviewTarget) this.reviewTarget.classList.toggle("d-none", changed)
    this.filter()
  }

  changeFilter(event) {
    this.activeFilter = event.currentTarget.dataset.filter
    this.filter()
  }

  filter() {
    const query = this.searchTarget.value.trim().toLocaleLowerCase()
    const unmappedOnly = this.activeFilter === "unmapped"
    this.filterTargets.forEach(button => {
      const active = button.dataset.filter === (this.activeFilter || "all")
      button.classList.toggle("is-active", active)
      button.setAttribute("aria-pressed", String(active))
    })
    let visible = 0
    this.rowTargets.forEach(row => {
      row.hidden = !row.dataset.fieldName.toLocaleLowerCase().includes(query) || (unmappedOnly && row.dataset.mapped === "true")
      if (!row.hidden) visible += 1
    })
    if (this.hasOptionalTarget) {
      const optionalVisible = [...this.optionalTarget.querySelectorAll('[data-import-mapping-target="row"]')].some(row => !row.hidden)
      this.optionalTarget.hidden = !optionalVisible
      if (query || unmappedOnly) {
        if (this.optionalWasOpen === undefined) this.optionalWasOpen = this.optionalTarget.open
        this.optionalTarget.open = true
      } else if (this.optionalWasOpen !== undefined) {
        this.optionalTarget.open = this.optionalWasOpen
        this.optionalWasOpen = undefined
      }
    }
    this.emptyTarget.textContent = unmappedOnly && !query ? "All fields are matched. You’re ready to import." : "No fields match this filter. Try another search."
    this.emptyTarget.classList.toggle("d-none", visible > 0)
  }

  autoMatch() {
    const normalize = value => value.toLocaleLowerCase().replace(/[^\p{L}\p{N}]/gu, "")
    let matched = 0
    this.selectTargets.forEach(select => {
      if (select.value || select.disabled) return
      const names = [normalize(select.dataset.label), normalize(select.dataset.field)]
      const options = [...select.options].filter(option => option.value && names.includes(normalize(option.value)))
      if (options.length === 1) {
        select.value = options[0].value
        matched += 1
      }
    })
    this.update()
    this.feedbackTarget.textContent = matched ? `Matched ${matched} additional ${matched === 1 ? "column" : "columns"}. Review the samples.` : "No additional matches found. Choose columns manually."
  }

  invalid(event) {
    this.searchTarget.value = ""
    this.activeFilter = "all"
    this.filter()
    const group = event.target.closest("details")
    if (group) group.open = true
  }

  submit(event) {
    if (this.submitting) {
      event.preventDefault()
      return
    }
    this.submitting = true
    const submitter = event.submitter || this.previewTarget
    const importing = submitter.dataset.importAction === "import"
    const label = importing ? "Queuing import…" : "Preparing preview…"
    if (submitter.tagName === "INPUT") submitter.value = label
    else submitter.textContent = label
    // Keep the submitter enabled so its native form action is preserved.
    submitter.setAttribute("aria-disabled", "true")
    const buttons = [this.previewTarget, this.directTarget, ...this.startTargets]
    buttons.forEach(button => {
      if (button !== submitter) button.disabled = true
    })
    this.feedbackTarget.textContent = importing ? "Saving your matches and starting the import…" : "Saving your matches and preparing the preview…"
  }

  schedulePoll() {
    this.pollTimer = window.setTimeout(() => this.poll(), 3000)
  }

  async poll() {
    this.pollRequest = new AbortController()
    try {
      const response = await fetch(this.statusUrlValue, {
        headers: { Accept: "text/html" }, credentials: "same-origin", signal: this.pollRequest.signal
      })
      if (response.ok) {
        const page = new DOMParser().parseFromString(await response.text(), "text/html")
        const replacement = page.getElementById("data_import_mapping")
        if (replacement && replacement.dataset.importMappingPreviewingValue !== "true") {
          this.element.replaceWith(replacement)
          return
        }
      }
    } catch (error) {
      if (error.name === "AbortError") return
    }
    if (this.element.isConnected) this.schedulePoll()
  }
}
